import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { GPA_LOGO } from "@/lib/assets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, CheckCircle2, ShieldAlert } from "lucide-react";

function BrandPanel() {
  return (
    <div className="brand-hero relative hidden w-1/2 flex-col justify-between p-12 text-white lg:flex">
      <div className="brand-mesh absolute inset-0" />
      <Link to="/" className="relative inline-flex items-center rounded-lg bg-white px-3 py-2">
        <img src={GPA_LOGO} alt="GPA+" className="h-9 w-auto" />
      </Link>
      <div className="relative">
        <h2 className="font-heading text-4xl font-extrabold leading-tight">The Transferable<br />Skills Benchmark</h2>
        <p className="mt-4 max-w-md text-slate-300">Choose a new password and you're back in.</p>
      </div>
      <p className="relative text-xs text-slate-400">FERPA-aligned · Role-based access · No real student PII</p>
    </div>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <BrandPanel />
      <div className="flex w-full items-center justify-center bg-slate-50 px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-md">
          <Link to="/login" className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#003057]">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>

          {!token ? (
            <div data-testid="reset-no-token">
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                <ShieldAlert className="h-6 w-6 text-amber-700" />
              </div>
              <h1 className="font-heading text-3xl font-extrabold text-[#003057]">Link incomplete</h1>
              <p className="mt-2 text-slate-600">This reset link is missing its token. Please request a new one.</p>
              <Link to="/forgot-password" className="mt-4 inline-block font-semibold text-[#005291] hover:text-[#78be21]">Request a new link</Link>
            </div>
          ) : done ? (
            <div data-testid="reset-done">
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#78be21]/15">
                <CheckCircle2 className="h-6 w-6 text-[#5fa114]" />
              </div>
              <h1 className="font-heading text-3xl font-extrabold text-[#003057]">Password reset</h1>
              <p className="mt-2 text-slate-600">Your password has been updated. Redirecting you to sign in…</p>
              <Link to="/login" className="mt-4 inline-block font-semibold text-[#005291] hover:text-[#78be21]">Go to sign in</Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-3xl font-extrabold text-[#003057]">Set a new password</h1>
              <p className="mt-1 text-slate-500">Choose a strong password you don't use elsewhere.</p>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <div>
                  <Label htmlFor="password">New password</Label>
                  <Input id="password" data-testid="reset-password" type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" required />
                </div>
                <div>
                  <Label htmlFor="confirm">Confirm new password</Label>
                  <Input id="confirm" data-testid="reset-confirm" type="password" value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" className="mt-1.5" required />
                </div>
                {error && <p data-testid="reset-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}
                <Button type="submit" data-testid="reset-submit" disabled={loading}
                  className="w-full rounded-full bg-[#003057] py-6 text-base font-bold hover:bg-[#004275]">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Reset password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
